import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import {
    VetRecentActivityClientModel, VetRuleOutcomeStepEnum, VetStatusEnum, VetTaskClientModel,
    CustomerDecisionStatusEnum,
    SafetyRatingReviewStatusEnum,
    OpenVetModel
} from "../../../../../../models/vetting/vetting.models";
import { Router } from "aurelia-router";
import { LookupValue, LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import * as Enumerable from "linq";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { VettingService } from "../../../../../../services/vetting/vetting.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { BaseVetOverview } from "./base-vet-overview";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { CustomerSubscriptionService } from "../../../../../../services/modules/customer-subscription-client.service";
import { Reflection } from "../../../../../../utilities/reflection";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { CustomerVesselService } from "../../../../../../services/vessels/customer-vessels.service";

@autoinject
@transient()
@useView('views/pages/modules/vetting/steps/overview/vet-overview.html')
export class VetOverview extends BaseVetOverview {

    public isEditingEmail: boolean;
    public originalEmail: string;
    private vetStatuses: LookupValue[];
    public canProcessCustomerDecision: boolean;
    public canApplyForWaiver: boolean;
    public showSTS: boolean;
    public showTC: boolean;
    public isRightShipUser: boolean;
    public isShowMoreSectionRelatedActivities: boolean;
    public viewCorrespondence: any;
    public canChangeShipManagerEmails: boolean;
    public reassignment: VetStatusEnum = VetStatusEnum.VetReassignment;
    public unacceptable: VetStatusEnum = VetStatusEnum.Rejected;
    public reloadItemFunc: any;
    public recent: VetRecentActivityClientModel[] = [];
    public canContactRightshipOperations: boolean;
    public canCancelCustomerDecisions: boolean;
    public forceLoad: boolean;
    private canCancelVet: boolean;
    public canViewRiskScoresInVetOverview: boolean;
    public riskScoreSeperatorShowing: boolean;
    public selectedOutcomes: number[] = [];
    public openVets: OpenVetModel[];

    async setCustomerDecision(customerDecisionStatusId: number) {
        try {
            await this.vettingClientService.setCustomerDecision(this.item.vetId, customerDecisionStatusId, this.decisionComments);
            this.item.customerDecisionStatusId = customerDecisionStatusId;
            this.notificationService.show("Customer Decision", "Successfully saved", NotificationType.Success);
        } catch {
            this.notificationService.show("Customer Decision", "Failed to set customer decision", NotificationType.Error);
        }
    }

    @computedFrom('item', 'item.vetTasks', 'item.vetTasks.length', 'selectedOutcomes', 'selectedOutcomes.length', 'task.vetSuperVetTaskOutcomeId')
    get vetTasks() {
        const tasks = {};
        if (this.item) {
            const filter = this.selectedOutcomes.length > 0;
            let vetTasks = this.item.vetTasks;
            const outcomeIds = this.selectedOutcomes
            if (filter) {
                const pending = outcomeIds.filter(x => x == -1).length > 0;
                vetTasks = vetTasks.filter(task => (pending && (!task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0)) || outcomeIds.indexOf(task.vetSuperVetTaskOutcomeId) > -1)
            }
            const items = Enumerable.from(vetTasks).groupBy(x => x.category ? x.category : x.isManual ? "Manual" : "No Category")
                .toArray();
            for (const item of items) {
                tasks[item.key()] = item.where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule).toArray();
            }
        }
        return tasks;
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.item),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks.length))
    get passed() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.Pass).length;
        }
        return 0;
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.item),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks.length))
    get failed() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.Fail).length;
        }
        return 0;
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.item),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks.length))
    get na() {
        if (this.item && this.item.vetTasks) {
            return this.item.vetTasks.filter(x => this.getOutcome(x) == VetRuleOutcomeStepEnum.NA).length;
        }
        return 0;
    }

    shouldShowContactRS(statusId: number) {

        let statuses = [];

        if (this.canContactRightshipOperations) {
            statuses = [VetStatusEnum.AwaitingResponse,
            VetStatusEnum.InProgress,
            VetStatusEnum.Pending,
            VetStatusEnum.PendingReview,
            VetStatusEnum.Processing,
            VetStatusEnum.ProcessingOutcome,
            VetStatusEnum.VetReassignment];
        }

        if (this.userClaimsService.hasPermission(Claims.CanContactRsOperationsWhenVetHasOutcome)) {
            statuses.push(VetStatusEnum.Completed);
            statuses.push(VetStatusEnum.Accepted);
            statuses.push(VetStatusEnum.Rejected);
            statuses.push(VetStatusEnum.Escalated);
        }

        return statuses.indexOf(statusId) > -1;
    }

    shouldShowSafetyScoreAndGhg(statusId: number) {
        return statusId == VetStatusEnum.Accepted || statusId == VetStatusEnum.Completed ||
            statusId == VetStatusEnum.Rejected || statusId == VetStatusEnum.Expired;
    }

    completed(statusId: number) {
        return statusId == VetStatusEnum.Accepted || statusId == VetStatusEnum.Completed || statusId == VetStatusEnum.ProcessingOutcome ||
            statusId == VetStatusEnum.Rejected || statusId == VetStatusEnum.Cancelled || statusId == VetStatusEnum.Expired;
    }

    @computedFrom("item", "item.vetStatusId")
    get showCustomerDecision() {
        return this.item && this.item.vetStatusId == VetStatusEnum.Escalated;
    }

    @computedFrom("item", "item.vetStatusId", "item.customerDecisionStatusId")
    get customerDecisionStatus() {
        if (this.item) {
            if (!this.item.requireCustomerDecision && !this.showCustomerDecision) return "";

            if (this.item.customerDecisionStatusId) {
                return this.getDecisionStatus(this.item.customerDecisionStatusId);
            }
        }
        return "Pending";
    }

    @computedFrom("item", "item.vetStatusId", "item.previousCustomerDecisionStatusId")
    get previousCustomerDecisionStatus() {
        if (this.item && this.item.previousCustomerDecisionStatusId) {
            return this.getDecisionStatus(this.item.previousCustomerDecisionStatusId);
        }
        return "";
    }

    getDecisionStatus(customerDecisionStatusId: number) {
        if (customerDecisionStatusId) {
            if (customerDecisionStatusId == CustomerDecisionStatusEnum.Acceptable) {
                return "Acceptable";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Unacceptable) {
                return "Unacceptable";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Pending) {
                return "Pending";
            }
            else if (customerDecisionStatusId == CustomerDecisionStatusEnum.Cancelled) {
                return "Cancelled";
            }
        }
    }

    get awaitingResponse() {
        return this.item && (this.item.vetStatusId == VetStatusEnum.AwaitingResponse);
    }

    get vetStatus() {
        if (this.vetStatuses && this.item)
            return this.vetStatuses.filter(x => x.id == this.item.vetStatusId)[0];

        return null;
    }

    @computedFrom("item", "item.vetStatusHistory", "item.vetStatusHistory.length", "item.vetStatusId")
    get lastVetStatusHistories() {
        const list = [this.item.vetStatusHistory[0]];

        if (this.showCustomerDecision && this.item.vetStatusHistory[1].statusId == VetStatusEnum.Rejected)
            list.push(this.item.vetStatusHistory[1]);

        list.forEach(vs => {
            vs.statusLabel = this.item.isVhr && vs.statusId == SafetyRatingReviewStatusEnum.New ? 'New' : vs.status;
        });

        return list;
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.lastVetStatusHistories),
        Reflection.getPropertyPath<VetOverview>(z => z.lastVetStatusHistories.length))
    get lastVetStatusHistoryUnacceptable() {
        return this.lastVetStatusHistories.filter(x => x.statusId == VetStatusEnum.Rejected)[0];
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.lastVetStatusHistories),
        Reflection.getPropertyPath<VetOverview>(z => z.lastVetStatusHistories.length))
    get lastVetStatusHistoryEscalated() {
        return this.lastVetStatusHistories.filter(x => x.statusId == VetStatusEnum.Escalated)[0];
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks),
        Reflection.getPropertyPath<VetOverview>(z => z.item.vetTasks.length))
    get canBeWaived() {
        const tasks = this.getTasks();
        return tasks ? tasks.filter(x => x.outcomeId == VetRuleOutcomeStepEnum.Fail).map(x => x.name) : []
    }

    @computedFrom(Reflection.getPropertyPath<VetOverview>(z => z.item.vetId),
    Reflection.getPropertyPath<VetOverview>(z => z.item.isHosted),
    Reflection.getPropertyPath<VetOverview>(z => z.item.isPassedToRSVettingQueue),
    Reflection.getPropertyPath<VetOverview>(z => z.item.liaseWithShipManagers))
    get canViewCorrespondence() {
        // if its a hosted vet and not passed to rs, cant see correspondence
        if (this.item.isHosted && !this.item.isPassedToRSVettingQueue) {
            return false
        }
        
        return this.item.liaseWithShipManagers ?? false;
        
    }

    @computedFrom("item", "item.initialEmailsForCorrespondence", "item.initialEmailsForCorrespondence.length")
    get shipManagerEmails() {
        if (this.item.initialEmailsForCorrespondence)
            return this.item.initialEmailsForCorrespondence.filter(x => x.isShipManagerEmail);
        return [];
    }

    @computedFrom("item", "item.vetForSTS")
    get vetForSTSValue(): string {
        if (this.item.vetForSTS == undefined || this.item.vetForSTS == null) {
            return '';
        } else if (this.item.vetForSTS) {
            return 'Yes'
        } else {
            return 'No'
        }
    }

    @computedFrom("item", "item.portCountryOfLoadSanctioned", "item.portCountryOfDischargeSanctioned")
    get isSanctionedCountry(): boolean {
        return this.item.portCountryOfLoadSanctioned || this.item.portCountryOfDischargeSanctioned;
    }

    @computedFrom("item", "item.portCountryOfDischargeUnknown", "item.portCountryOfLoadUnknown")
    get isUnknownCountry(): boolean {
        return this.item.portCountryOfDischargeUnknown || this.item.portCountryOfLoadUnknown;
    }

    @computedFrom("item", "item.vetStatusId")
    get notProcessing() {
        return this.item && this.item.vetStatusId != VetStatusEnum.Processing;
    }

    @computedFrom('item', 'item.vetTasks')
    get vetTaskKeys() {
        if (this.item)
            return Enumerable.from(this.item.vetTasks).where(x => !x.isVisibleToRsVetSupersOnly && !x.isInfoOnlyRule)
                .orderBy(x => x.categoryIsCustomer ? 1 : 2).select(x => x.category ? x.category : x.isManual ? "Manual" : "No Category").distinct().toArray();
        return [];
    }

    constructor(private vettingClientService: VettingService, signalClientService: SignalClientService, manageVesselService: ManageVesselService,
        notificationsService: NotificationClientService, lookupClientService: LookupClientService,
        userClaimsService: UserClaimsService, dialogClientService: DialogClientService,
        i18N: I18N, router: Router, userClientService: UserClientService,
        customerSubscriptionService: CustomerSubscriptionService,
        customerVesselsService: CustomerVesselService, configService: ConfigService
    ) {
        super(notificationsService, i18N, vettingClientService, dialogClientService, userClaimsService, signalClientService,
            manageVesselService, router, userClientService,
            customerSubscriptionService, customerVesselsService, configService);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        lookupClientService.getLookup(request).then(x => {
            this.vetStatuses = x.activeValues;
        });
        this.canProcessCustomerDecision = this.userClaimsService.hasPermission(Claims.CanProcessCustomerDecision);
        this.canApplyForWaiver = this.userClaimsService.hasPermission(Claims.CanApplyForWaiver);
        this.showBerthInVetRequest = this.userClaimsService.hasPermission(Claims.ShowBerthInVetRequest);
        this.showSTS = this.userClaimsService.hasPermission(Claims.ShowShipToShip);
        this.showTC = this.userClaimsService.hasPermission(Claims.ShowCharterType);
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canChangeShipManagerEmails = this.userClaimsService.hasPermission(Claims.CanChangeShipManagerEmails);
        this.canContactRightshipOperations = this.userClaimsService.hasPermission(Claims.CanContactRightshipOperations);
        this.canCancelCustomerDecisions = this.userClaimsService.hasPermission(Claims.CanCancelCustomerDecisions);
        this.canCancelVet = this.userClaimsService.hasPermission(Claims.CanCancelVet);
        this.canViewRiskScoresInVetOverview = this.userClaimsService.hasPermission(Claims.CanViewRiskScoresInVetOverview);
        this.isAutoScreen = false;
    }

    async attached() {
        super.attached();

        //SignalR
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Vet), this.onVetStatusChanged);

        //Load open vets
        this.loadOpenVets();
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Vet), this.onVetStatusChanged);
    }

    get showUserCancellation() {
        const allowedStatuses = [VetStatusEnum.Pending, VetStatusEnum.Processing, VetStatusEnum.InProgress, VetStatusEnum.PendingReview, VetStatusEnum.AwaitingResponse];
        return this.item && allowedStatuses.indexOf(this.item.vetStatusId) > -1 && this.canCancelVet;
    }

    inspectionAsOf(): Date[] {
        return this.lastVetStatusHistories.map(x => this.completed(x.statusId) ? x.createdDate : null);
    }

    cancelVet() {
        const taskOutcomes = this.item.vetTasks.filter(x => x.vetSuperVetTaskOutcomeId).map(x => x.vetSuperVetTaskOutcomeId);
        const unsetRules = this.item.vetTasks.filter(x => !x.vetSuperVetTaskOutcomeId && !x.isInfoOnlyRule).length;
        const vetSuperFinished = unsetRules == 0;

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VetStatusDialog,
            {
                isVhr: this.item.isVhr,
                requireCustomerDecision: this.item.requireCustomerDecision,
                vetSuperFinished: vetSuperFinished,
                passed: taskOutcomes.filter(x => x == VetRuleOutcomeStepEnum.Pass).length,
                failed: taskOutcomes.filter(x => x == VetRuleOutcomeStepEnum.Fail).length,
                na: taskOutcomes.filter(x => x == VetRuleOutcomeStepEnum.NA).length,
                noVetSuperOutcome: unsetRules,
                otherComments: this.item.otherComments,
                tasks: [],
                allTasks: [],
                vetId: this.item.vetId,
                vetStatusId: this.item.vetStatusId,
                onDraftGenerated: this.reloadItemFunc,
                onSubmit: this.reloadItemFunc,
                vesselName: this.item.vesselName,
                internationalMaritimeOrganizationNumber: this.item.internationalMaritimeOrganizationNumber,
                europeanNumberOfIdentification: this.item.europeanNumberOfIdentification,
                vehicleIdentificationNumber: this.item.vehicleIdentificationNumber,
                subtaskTypes: [],
                cancelOnly: true
            }, true, false, false, 'right');
    }

    protected onActivated() {
        this.setCategories();
        this.loadRecentActivities();
    }

    async loadRecentActivities() {
        if (this.item) {
            this.recent = await this.vettingClientService.getVetRecentActivity(this.item.vetId, false, false);
        }
    }

    isLink(type: string) {
        return this.vettingClientService.isLink(type);
    }

    generateRecentLink(title: string, id: number, type: string) {
        return this.vettingClientService.generateRecentLink(this.item.vetId, title, id, type);
    }

    isCategoryCustomer(cat: string) {
        return Enumerable.from(this.item.vetTasks).where(x => x.category == cat).any(x => x.categoryIsCustomer);
    }

    getTitle(taskGroup) {
        const x = Number(taskGroup) as VetRuleOutcomeStepEnum;
        switch (x) {
            case VetRuleOutcomeStepEnum.Pass:
                return "Passed Rules";
            case VetRuleOutcomeStepEnum.Fail:
                return "Failed Rules";
            case VetRuleOutcomeStepEnum.NA:
                return "Not Applicable Rules";
        }
    }

    filterClick(outcome: { id: number; name: string }) {
        const index = this.selectedOutcomes.indexOf(outcome.id);
        if (index >= 0) {
            this.selectedOutcomes.splice(index, 1);
        }
        else {
            this.selectedOutcomes.push(outcome.id);
        }
    }

    private getOutcome(x: VetTaskClientModel) {
        return x.vetSuperVetTaskOutcomeId ? x.vetSuperVetTaskOutcomeId : x.vetTaskOutcomeId;
    }

    categories: { id: number; name: string }[] = [];

    @computedFrom('item', 'item.vetTasks', 'task.vetSuperVetTaskOutcomeId')
    get outcomes(): { id: number; name: string }[] {
        const list = [];
        if (this.item && this.item.vetTasks) {
            const vetTasks: VetTaskClientModel[] = this.item.vetTasks;
            const pending = vetTasks.filter(task => (!task.vetSuperVetTaskOutcomeId || task.vetSuperVetTaskOutcomeId <= 0) && !task.isInfoOnlyRule).length;
            const passed = vetTasks.filter(task => task.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass).length;
            const failed = vetTasks.filter(task => task.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail).length;
            const notApplicable = vetTasks.filter(task => task.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.NA).length;

            list.push({ id: -1, name: `Pending (${pending})` });
            list.push({ id: VetRuleOutcomeStepEnum.Pass, name: `Passed (${passed})` });
            list.push({ id: VetRuleOutcomeStepEnum.Fail, name: `Failed (${failed})` });
            list.push({ id: VetRuleOutcomeStepEnum.NA, name: `Not Applicable (${notApplicable})` });
        }

        return list;
    }

    setCategories() {
        const vetTasks: VetTaskClientModel[] = Enumerable.from(this.vetTasks).selectMany(x => <VetTaskClientModel[]>x.value).toArray();
        this.categories = Enumerable.from(vetTasks.map(x => {
            return {
                id: x.isManual ? -1 : x.categoryId,
                name: x.isManual ? "Manual" : x.category
            };
        })).groupBy(x => x.id).select(x => x.firstOrDefault()).where(x => x != null).toArray();
    }

    showContactVetSuperDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddCorrespondenceDialog, {
            entityId: this.item.vetId,
            entityTypeId: EntityTypes.Vet
        }, true, false, false, "right");
    }

    getTasks() {
        return this.item.vetTasks
            .map(x => {
                return {
                    vetTaskId: x.vetTaskId,
                    name: x.title,
                    comment: x.isInfoOnlyRule ? x.infoVettingComment
                        : x.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Pass ? x.passedVettingComment
                            : x.vetSuperVetTaskOutcomeId == VetRuleOutcomeStepEnum.Fail ? x.failedVettingComment
                                : x.naVettingComment,
                    passedComment: x.passedVettingComment,
                    failedComment: x.failedVettingComment,
                    forceIncludeComment: x.forceIncludeComment,
                    isInfoOnlyRule: x.isInfoOnlyRule,
                    infoComment: x.infoVettingComment,
                    naComment: x.naVettingComment,
                    outcomeId: x.vetSuperVetTaskOutcomeId
                };
            })
    }

    showApplyForWaiverDialog() {
        if (!this.item || this.item.vetStatusId != this.unacceptable) return;

        if (this.canApplyForWaiver) {
            const taskOutcomes = this.item.vetTasks.filter(x => x.vetSuperVetTaskOutcomeId).map(x => x.vetSuperVetTaskOutcomeId);
            const unsetRules = this.item.vetTasks.filter(x => !x.vetSuperVetTaskOutcomeId && !x.isInfoOnlyRule).length;
            const vetSuperFinished = unsetRules == 0;
            const tasks = this.getTasks();

            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ApplyForWaiverDialog,
                {
                    requireCustomerDecision: true,
                    vetSuperFinished: vetSuperFinished,
                    passed: taskOutcomes.filter(x => x == VetRuleOutcomeStepEnum.Pass).length,
                    failed: taskOutcomes.filter(x => x == VetRuleOutcomeStepEnum.Fail).length,
                    na: taskOutcomes.filter(x => x == VetRuleOutcomeStepEnum.NA).length,
                    noVetSuperOutcome: unsetRules,
                    otherComments: this.item.otherComments,
                    tasks: tasks,
                    vetId: this.item.vetId,
                    vetStatusId: this.item.vetStatusId,
                    onDraftGenerated: this.reloadItemFunc,
                    onSubmit: this.reloadItemFunc,
                    vesselName: this.item.vesselName,
                    internationalMaritimeOrganizationNumber: this.item.internationalMaritimeOrganizationNumber,
                    europeanNumberOfIdentification: this.item.europeanNumberOfIdentification,
                    vehicleIdentificationNumber: this.item.vehicleIdentificationNumber,
                    subtaskTypes: [],
                }, true, false, false, 'right');
        }
    }

    changeShipManagerEmails() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ChangeShipManagerEmail, {
            vessel: this.vessel,
            vet: this.item,
            onConfirm: (() => {
                if (this.reloadItemFunc)
                    this.reloadItemFunc();
            }).bind(this)
        }, true, false, false);
    }

    @computedFrom("lastVetStatusHistory", "lastVetStatusHistory.user")
    get lastVetStatusHistoryUsers() {
        const systemStatuses = [
            VetStatusEnum.Processing,
            VetStatusEnum.ProcessingOutcome,
            VetStatusEnum.Pending,
            VetStatusEnum.PendingReview,
            VetStatusEnum.Expired
        ];
        const users = this.lastVetStatusHistories.map(x => {
            let user = x.user;
            if (!user && (systemStatuses.indexOf(x.statusId) > -1
                || (x.statusId == VetStatusEnum.Cancelled && x.isAutoCancelled == true))) user = "System";
            return user;
        });
        return users;
    }

    showMoreSectionRelatedActivities() {
        this.isShowMoreSectionRelatedActivities = !this.isShowMoreSectionRelatedActivities;
    }

    openContactDetailsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ContactDetailsDialog, {
            model: this.item,
            onConfirm: (() => {
                if (this.reloadItemFunc)
                    this.reloadItemFunc();
            }).bind(this)
        },
            true, false, false,)
    }

    //Show seperator the first time its not "Critical" or "Soft" rule within the loop
    showSeperatorForRiskScores(risk: string) {
        if (risk == "Critical") {
            this.riskScoreSeperatorShowing = true;
        }
        else if (risk == "Soft") {
            this.riskScoreSeperatorShowing = true;
        }
        else if (this.riskScoreSeperatorShowing) {
            return true;
        }
    }

    onVetStatusChanged = (data: EntityStatusChangeModel) => {
        if (data.vesselId == this.item.vesselId) {
            this.loadOpenVets();
        }
    }

    //Load open vets
    loadOpenVets = () => {
        return new Promise<void>((resolve, reject) => {
            const vesselId = this.item?.vesselId;
            this.vettingClientService.getOpenVetsForVessel(vesselId).then(openVets => {
                this.openVets = openVets.filter(x => x.vetId != this.item?.vetId);
                resolve();
            }).catch(e => {
                console.error("Failed to load open vets. error: " + e.message);
                reject(e);
            });
        });
    }
}